import React, { memo, useMemo } from "react";
import { North, South } from "@mui/icons-material";
import {
  TableContainer,
  Paper,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Stack,
  Avatar,
  Box,
  Button,
  Typography,
  Tooltip,
} from "@mui/material";
import { keyframes } from "@mui/system";
import { tableCellClasses } from "@mui/material/TableCell";
import Collapse from "@mui/material/Collapse";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import IconButton from "@mui/material/IconButton";
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import InstaCollapes from "./instaCollapes";
import YoutubeCollapes from "./youtubeCollapes";
import "../css/dashboard.css";
import GetCookieByName from "../../../hooks/GetCookie";
import jwtDecode from "jwt-decode";
import helpers from "../../../hooks/HelperFunctions";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useNavigate } from "react-router-dom";
// Mirrors RATING_META in CreatorRemarksTab — keeps labels in sync across both views
const RATING_BADGE_MAP = {
  1: { icon: '⚑', color: '#DC2626', bg: 'rgba(220,38,38,0.12)',  border: 'rgba(220,38,38,0.28)',  label: 'Poor'          },
  2: { icon: '⚑', color: '#EA580C', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.28)', label: 'Below Average' },
  3: { icon: '◉', color: '#D97706', bg: 'rgba(217,119,6,0.12)',  border: 'rgba(217,119,6,0.28)',  label: 'Average'       },
  4: { icon: '✦', color: '#059669', bg: 'rgba(5,150,105,0.12)',  border: 'rgba(5,150,105,0.28)',  label: 'Good'          },
  5: { icon: '★', color: '#4F46E5', bg: 'rgba(99,102,241,0.12)', border: 'rgba(99,102,241,0.28)', label: 'Excellent'     },
};

const GENDER_STYLES = {
  M: { background: "#DBEAFE", color: "#1E40AF" },
  F: { background: "#FCE7F3", color: "#9D174D" },
};
const DEFAULT_GENDER_STYLE = { background: "#F3F4F6", color: "#374151" };
const getGenderStyle = (gender) => GENDER_STYLES[gender] ?? DEFAULT_GENDER_STYLE;

const AVATAR_PALETTE = [
  { bg: '#FFE8D6', color: '#FF5200' },
  { bg: '#D4EDDA', color: '#1A6832' },
  { bg: '#CCE5FF', color: '#004085' },
  { bg: '#E2D9F3', color: '#5B2D8E' },
  { bg: '#FFEEBA', color: '#7A5A00' },
  { bg: '#D1ECF1', color: '#0C5460' },
  { bg: '#F8D7DA', color: '#7A1C22' },
  { bg: '#D6EAF8', color: '#1A5276' },
];

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const getYTChannelTitle = (ytData) => {
  return ytData?.title || ytData?.handle || ytData?.channel_id || ""
};

const Row = memo(function Row({
  profile,
  index,
  animationIndex,
  colorIndex,
  filters,
  numbers,
  exportList,
  credits,
  contactFormatter,
  categoryFormatter,
  handleCheckboxClick,
  onDelete,
  user,
  engagedCreators,
}) {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const isYoutube = filters?.platform === "Youtube";
  // Catches both newly-tagged records (youtube: true) and legacy records where the
  // channel ID (UC + 22 chars) was mistakenly stored as username.
  const isYoutubeCreator = !!(profile.youtube || /^UC[a-zA-Z0-9_-]{22}$/.test(profile.username || ''));
  const profileIdentifier = isYoutube
    ? profile.youtubeData?.channel_id
    : profile.username;

  const matchedNumberEntry = numbers.find((list) => {
    if (isYoutube) {
      return list.youtubeData?.channel_id === profileIdentifier;
    }
    return list.username === profile.username;
  });
  // `numbers` comes from users/viewedInfluencers, which now returns `contactNumbers`
  // (array) instead of a scalar `.number` — helper handles both shapes.
  const hasNumber = !!helpers.getPrimaryContactNumber(matchedNumberEntry);

  // Which platform this row's creator is on (the list can mix them when searching with no platform).
  const rowPlatform = profile.platform === "twitter"
    ? { label: "X", bg: "#E7E7E7", color: "#000" }
    : profile.platform === "linkedin"
      ? { label: "LinkedIn", bg: "#DCEBFA", color: "#0A66C2" }
      : isYoutube || isYoutubeCreator
        ? { label: "YouTube", bg: "#FDE2E2", color: "#C4302B" }
        : { label: "Instagram", bg: "#FCE4F1", color: "#C13584" };

  const displayName = (isYoutube
    ? getYTChannelTitle(profile.youtubeData)
    : isYoutubeCreator
      ? (profile.name || profile.username)
      : profile.username) || '';
  const initials = (displayName || "").charAt(0).toUpperCase() + (displayName || "").charAt(1).toLowerCase();

  const isDisabled = credits !== null && credits !== undefined && credits === 0 && !hasNumber;
  const { background: genderBg, color: genderColor } = getGenderStyle(profile.gender);
  const avatarTheme = AVATAR_PALETTE[(colorIndex ?? animationIndex) % AVATAR_PALETTE.length];

  const ratingBadge = React.useMemo(() => {
    if (!profile.ratingCount) return null;
    const avg = profile.avgRating || 0;
    const rounded = Math.max(1, Math.min(5, Math.round(avg)));
    const cfg = RATING_BADGE_MAP[rounded];
    return { ...cfg, tip: `${cfg.label} · ${avg.toFixed(1)}/5 avg (${profile.ratingCount} rating${profile.ratingCount !== 1 ? 's' : ''})` };
  }, [profile.ratingCount, profile.avgRating]);

  return (
    <>
      <TableRow
        sx={{
          borderBottom: "0.5px solid lightgray",
          minHeight: 24,
          animation: `${fadeSlideIn} 0.25s ease both`,
          animationDelay: `${Math.min(animationIndex * 0.04, 0.4)}s`,
          transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            backgroundColor: '#FFF5F0',
            boxShadow: 'inset 3px 0 0 #FF5200',
          },
        }}
      >
        <TableCell style={{ width: "5%" }}>
          <input
            type="checkbox"
            id={profileIdentifier}
            title="Select"
            checked={exportList.includes(profileIdentifier)}
            style={{ width: "18px", height: "18px", cursor: "pointer" }}
            onChange={(event) => {
              if (event.target.disabled) return;
              handleCheckboxClick(event, profileIdentifier, profile._id);
            }}
            disabled={isDisabled}
          />
        </TableCell>

        <TableCell style={{ width: "15%", fontFamily: 'Quicksand', fontWeight: 600 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar
              sx={{
                width: 30,
                height: 30,
                fontSize: 11,
                fontWeight: 800,
                bgcolor: avatarTheme.bg,
                color: avatarTheme.color,
                transition: 'transform 0.2s ease',
                '&:hover': { transform: 'scale(1.18)' },
              }}
            >
              {initials}
            </Avatar>
            {isYoutube ? (
              <a
                href={
                  profile.youtubeData?.handle
                    ? `https://www.youtube.com/${profile.youtubeData.handle}`
                    : profile.youtubeData?.channel_id
                      ? `https://www.youtube.com/channel/${profile.youtubeData.channel_id}`
                      : profile.youtubeData?.url
                        ? profile.youtubeData.url
                        : ""
                }
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "#FF5200", textTransform: 'capitalize' }}
              >
                {getYTChannelTitle(profile.youtubeData)}
              </a>
            ) : isYoutubeCreator ? (
              // YouTube creator stored from a report — use /channel/{id} (always resolves)
              <a
                href={
                  /^UC[a-zA-Z0-9_-]{22}$/.test(profile.username)
                    ? `https://www.youtube.com/channel/${profile.username}`
                    : `https://www.youtube.com/@${profile.username}`
                }
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "#FF5200", display: 'flex', gap: '4px' }}
              >
                {displayName}
              </a>
            ) : profile.platform === "twitter" || profile.platform === "linkedin" ? (
              // X / LinkedIn creators: plain handle or slug ("company/<slug>" for a company page).
              <a
                href={
                  profile.twitterLink || profile.linkedinUrl ||
                  (profile.platform === "twitter"
                    ? `https://x.com/${profile.username}`
                    : `https://www.linkedin.com/${/^company\//.test(profile.username) ? "" : "in/"}${profile.username}`)
                }
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "#FF5200", display: 'flex', gap: '4px' }}
              >
                {profile.name || profile.username}
              </a>
            ) : (
              <a
                href={`https://www.instagram.com/${profile.username}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "#FF5200", display: 'flex', gap: '4px' }}
              >
                {profile.username}
                {/* {profile.is_verified &&(
                   <VerifiedIcon sx={{ fontSize: 14, color: "#1D9BF0" , flexShrink: 0 }} />
                )} */}
              </a>
            )}
            <span
              style={{
                background: rowPlatform.bg,
                color: rowPlatform.color,
                fontSize: 10,
                fontWeight: 700,
                padding: "1px 6px",
                borderRadius: 8,
                whiteSpace: "nowrap",
              }}
            >
              {rowPlatform.label}
            </span>
          </Stack>
        </TableCell>

        <TableCell style={{ width: "10%", fontFamily: 'Quicksand', fontWeight: 600 }}>
          <Avatar
            title={profile.gender === 'M' ? 'Male' : profile.gender === 'F' ? 'Female' : (profile.gender || 'Other')}
            sx={{ width: 25, height: 25, fontSize: 11, bgcolor: genderBg, color: genderColor }}
          >
            {profile.gender}
          </Avatar>
        </TableCell>

        <TableCell style={{ width: "15%", fontFamily: 'Quicksand', fontWeight: 600 }}>
          {filters?.platform === "Youtube"
            ? helpers.formatNumber(profile?.youtubeData?.statistics?.subscriberCount, false)
            : helpers.formatNumber(profile.instaFollowers, false)}
        </TableCell>
        <TableCell style={{ width: "10%" }}>
          {categoryFormatter(
            profile.category ? profile.category : profile.categoryName
          )}
        </TableCell>
        <TableCell style={{ textAlign: "end", width: "15%" }}>
          {contactFormatter(profile._id, profile.isNumber, profile.username, profile)}
        </TableCell>
        <TableCell style={{ width: "10%" }}>
          <Stack direction="row">
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? (
                <KeyboardArrowUpIcon fontSize="18" htmlColor="#384959" />
              ) : (
                <KeyboardArrowDownIcon fontSize="18" htmlColor="#384959" />
              )}
            </IconButton>

            {(engagedCreators || (profile.paymentsDone?.length > 0) || profile.hasReport || profile.igConversations?.length > 0) && (
              <IconButton
                size="small"
                onClick={() =>
                  
                  {
                    navigate(`/payment-history/${profile.id}`)
                  }
              
                }
                title="View payment history"
              >
                <CurrencyRupeeIcon fontSize="18" htmlColor="#E35336" />
              </IconButton>
            )}

            {ratingBadge && (
              <Tooltip
                title={`${ratingBadge.label} · click to open Remarks`}
                placement="top"
                arrow
              >
                <Box
                  onClick={() => window.open(`/payment-history/${profile.id}?tab=remarks`, '_blank', 'noopener,noreferrer')}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: '3px',
                    px: '7px', py: '3px', borderRadius: 99, flexShrink: 0,
                    background: ratingBadge.bg,
                    border: `1.5px solid ${ratingBadge.border}`,
                    cursor: 'pointer', userSelect: 'none',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: `0 4px 10px ${ratingBadge.border}`,
                      filter: 'brightness(1.06)',
                    },
                  }}
                >
                  {/* Gold star — same colour regardless of sentiment */}
                  <Box component="span" sx={{ fontSize: 11, color: '#C8850E', lineHeight: 1 }}>★</Box>
                  <Box component="span" sx={{
                    fontSize: 11, fontWeight: 700, lineHeight: 1,
                    color: ratingBadge.color, fontFamily: 'Quicksand, sans-serif',
                    letterSpacing: 0.2,
                  }}>
                    {(profile.avgRating || 0).toFixed(1)}
                  </Box>
                  <Box component="span" sx={{
                    fontSize: 10, lineHeight: 1,
                    color: ratingBadge.color, fontFamily: 'Quicksand, sans-serif',
                    opacity: 0.75, fontWeight: 600,
                  }}>
                    {ratingBadge.label}
                  </Box>
                </Box>
              </Tooltip>
            )}

            {user.isSuperUser ? (
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => onDelete(profile.id)}
                title="Delete Creator"
              >
                <DeleteForeverIcon fontSize="18" htmlColor="#9B1313" />
              </IconButton>
            ) : null}
          </Stack>
        </TableCell>
      </TableRow>

      <TableRow key={`inner_${index}`}>
        <TableCell colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <InstaCollapes profile={profile} numbers={numbers} />
            {profile.isYoutube && <YoutubeCollapes profile={profile} />}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
});

const TableComponent = memo(function TableComponent({
  filters,
  numbers,
  profiles,
  contactFormatter,
  categoryFormatter,
  handleCheckboxClick,
  credits,
  exportList,
  onSort,
  tableSort,
  userId,
  onDelete,
  onDeselectAll,
  onOpenAddToPlan,
  engagedCreators,
}) {
  const user = useMemo(() => {
    try {
      const token = GetCookieByName('auth');
      if (typeof token === 'string') {
        return jwtDecode(JSON.parse(token)['token']);
      }
    } catch { }
    return { isSuperUser: false };
  }, []);

  return (
    <>
      {exportList.length > 0 && (
        <Box sx={{
          position: 'sticky',
          top: { xs: 104, sm: 116 },
          zIndex: 200,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1,
          mb: 0.75,
          background: 'linear-gradient(90deg, #FFF5F0 0%, #FFE8D6 100%)',
          border: '1px solid #FFD4B8',
          borderRadius: 1.5,
          boxShadow: '0 3px 14px rgba(255,82,0,0.15)',
        }}>
          <Typography variant="body2" sx={{ fontFamily: 'Quicksand', fontWeight: 700, color: '#FF5200' }}>
            {exportList.length} creator{exportList.length !== 1 ? 's' : ''} selected
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              onClick={onOpenAddToPlan}
              startIcon={<PlaylistAddIcon sx={{ fontSize: 17 }} />}
              sx={{
                fontFamily: 'Quicksand',
                fontWeight: 700,
                color: '#fff',
                background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)',
                borderRadius: '20px',
                textTransform: 'none',
                fontSize: '0.75rem',
                boxShadow: '0 2px 10px rgba(255,82,0,0.28)',
                '&:hover': { background: 'linear-gradient(135deg, #CC4200 0%, #E07800 100%)', boxShadow: '0 3px 14px rgba(255,82,0,0.4)' },
              }}
            >
              Add to Plan
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={onDeselectAll}
              sx={{
                fontFamily: 'Quicksand',
                fontWeight: 600,
                color: '#FF5200',
                borderColor: '#FF5200',
                borderRadius: '20px',
                textTransform: 'none',
                fontSize: '0.75rem',
                '&:hover': { borderColor: '#CC4200', color: '#CC4200', bgcolor: 'rgba(255,82,0,0.04)' },
              }}
            >
              Deselect All
            </Button>
          </Box>
        </Box>
      )}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <Table
          sx={{
            minWidth: 650,
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "none",
            }
          }}
          aria-label="simple table"
          size="small"
        >
          <TableHead>
            <TableRow
              key={"unique"}
              sx={{ background: 'linear-gradient(135deg, #1C1C2E 0%, #2D1A5E 100%)' }}
            >
              <TableCell sx={{ borderBottom: 'none' }}></TableCell>
              <TableCell style={{ fontWeight: 700, fontFamily: 'Lato', color: 'white', letterSpacing: 0.4 }} sx={{ borderBottom: 'none' }}>
                {filters?.platform === "Youtube" ? "Channel Name" : "User Name"}
              </TableCell>
              <TableCell
                style={{ fontWeight: 700, fontFamily: 'Lato', color: 'white', letterSpacing: 0.4 }}
                sx={{ cursor: "pointer", borderBottom: 'none', '&:hover': { color: '#FFB380' } }}
                onClick={() => onSort("gender")}
              >
                {tableSort.gender ? (
                  tableSort.gender === "asc" ? (
                    <North sx={{ fontSize: "12px", color: '#FFB380' }} />
                  ) : (
                    <South sx={{ fontSize: "12px", color: '#FFB380' }} />
                  )
                ) : null}
                Gender
              </TableCell>
              <TableCell
                style={{ fontWeight: 700, fontFamily: 'Lato', color: 'white', letterSpacing: 0.4 }}
                sx={{ cursor: "pointer", borderBottom: 'none', '&:hover': { color: '#FFB380' } }}
                onClick={() =>
                  onSort(
                    filters?.platform === "Youtube" ? "Subscribers" : "followers"
                  )
                }
              >
                {tableSort.followers ? (
                  tableSort.followers === "asc" ? (
                    <North sx={{ fontSize: "12px", color: '#FFB380' }} />
                  ) : (
                    <South sx={{ fontSize: "12px", color: '#FFB380' }} />
                  )
                ) : null}
                {filters?.platform === "Youtube" ? "Subscribers" : "Followers"}
              </TableCell>
              <TableCell style={{ fontWeight: 700, fontFamily: 'Lato', color: 'white', letterSpacing: 0.4 }} sx={{ borderBottom: 'none' }}>Category</TableCell>
              <TableCell style={{ fontWeight: 700, fontFamily: 'Lato', color: 'rgba(255,255,255,0.5)' }} sx={{ borderBottom: 'none' }}></TableCell>
              <TableCell sx={{ borderBottom: 'none' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {profiles.map((profile, index) => (
              <Row
                key={profile.id || index}
                profile={profile}
                index={profile.id || index}
                animationIndex={index}
                colorIndex={index}
                filters={filters}
                numbers={numbers}
                exportList={exportList}
                credits={credits}
                contactFormatter={contactFormatter}
                categoryFormatter={categoryFormatter}
                handleCheckboxClick={handleCheckboxClick}
                onDelete={onDelete}
                user={user}
                engagedCreators={engagedCreators}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
});

export default TableComponent;
